import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar, CheckCircle2, AlertTriangle, Clock, Info, ArrowRight,
  Sparkles, ExternalLink, ShieldCheck,
} from "lucide-react";
import api from "@/lib/api";

/**
 * VisaBulletinCheckerPage — free public priority-date checker.
 *
 * NO signup required. Users enter their priority date + category +
 * country → instantly see their status against the current State
 * Department Visa Bulletin. Powered by /api/visa-bulletin/check.
 *
 * SEO targets:
 *   - "check my priority date"
 *   - "F4 Philippines wait time"
 *   - "EB-2 India visa bulletin"
 *   - "priority date current"
 *   - "visa bulletin calculator"
 *
 * Route: /visa-bulletin-checker
 */

const CATEGORIES = [
  { id: "F1",   label: "F1 — Unmarried adult children of U.S. citizens" },
  { id: "F2A",  label: "F2A — Spouses & minor children of LPRs" },
  { id: "F2B",  label: "F2B — Unmarried adult children of LPRs" },
  { id: "F3",   label: "F3 — Married children of U.S. citizens" },
  { id: "F4",   label: "F4 — Brothers & sisters of U.S. citizens" },
  { id: "EB1",  label: "EB-1 — Priority workers (extraordinary ability, professors)" },
  { id: "EB2",  label: "EB-2 — Advanced-degree professionals / NIW" },
  { id: "EB3",  label: "EB-3 — Skilled workers, professionals" },
  { id: "EB3O", label: "EB-3 Other Workers" },
  { id: "EB4",  label: "EB-4 — Special immigrants" },
  { id: "EB5",  label: "EB-5 — Investors" },
];

const COUNTRIES = [
  { id: "ALL", label: "All other countries" },
  { id: "CHINA", label: "China (mainland)" },
  { id: "INDIA", label: "India" },
  { id: "MEXICO", label: "Mexico" },
  { id: "PHILIPPINES", label: "Philippines" },
];

// Headline for the result card — early-return style instead of nested ternaries.
function headlineFor(result) {
  if (result.fad_status === "current") return "Your priority date is CURRENT";
  if (result.fad_status === "unavailable") return "Category is UNAVAILABLE";
  if (result.fad_status === "backlogged") {
    if (result.estimated_wait_months >= 24) return `~${Math.round(result.estimated_wait_months / 12)} years behind`;
    return `~${result.estimated_wait_months} months behind`;
  }
  return "Awaiting Visa Bulletin";
}

const SEVERITY = {
  success:  { bg: "#F0FDF4", border: "#BBF7D0", text: "#166534", icon: CheckCircle2, badge: "CURRENT" },
  info:     { bg: "#EFF6FF", border: "#BFDBFE", text: "#1E40AF", icon: Info,           badge: "Backlogged" },
  warning:  { bg: "#FFFBEB", border: "#FED7AA", text: "#B45309", icon: AlertTriangle,  badge: "Unavailable" },
  attention:{ bg: "#FEF2F2", border: "#FECACA", text: "#B91C1C", icon: AlertTriangle,  badge: "Needs input" },
};

// Set SEO metadata on mount
function useSeoMeta() {
  useEffect(() => {
    document.title = "Visa Bulletin Priority Date Checker (Free) — DS-260USA.com";
    const set = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    set(
      "description",
      "Free instant priority-date checker. Enter your priority date, preference category, and country of chargeability — we compare against the current U.S. State Department Visa Bulletin and tell you if you're current, backlogged, or unavailable. No signup needed."
    );
    set("keywords", "visa bulletin, priority date, F4 Philippines, EB-2 India, EB-3 China, F2A, priority date checker, visa bulletin calculator, immigrant visa wait time, green card wait time");
    return () => { document.title = "DS-260USA.com"; };
  }, []);
}

export default function VisaBulletinCheckerPage() {
  useSeoMeta();
  const [form, setForm] = useState({ priority_date: "", category: "F4", country: "PHILIPPINES" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const check = async (e) => {
    e?.preventDefault?.();
    if (!form.priority_date || !form.category) {
      setErr("Enter a priority date and choose a category first.");
      return;
    }
    setBusy(true); setErr(""); setResult(null);
    try {
      const { data } = await api.post("/visa-bulletin/check", {
        priority_date: form.priority_date,
        category: form.category,
        country: form.country,
      });
      setResult(data);
    } catch (e) {
      setErr("Could not check right now. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const sev = result ? (SEVERITY[result.banner_severity] || SEVERITY.info) : null;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-app)" }} data-testid="visa-checker-page">
      {/* Top bar */}
      <header
        className="border-b sticky top-0 z-10"
        style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}
      >
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display font-extrabold text-lg" style={{ color: "var(--text-primary)" }}>
            DS-260USA<span style={{ color: "var(--action)" }}>.com</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/faq" style={{ color: "var(--text-secondary)" }}>FAQ</Link>
            <Link to="/sample-packet" style={{ color: "var(--text-secondary)" }}>Sample packet</Link>
            <Link to="/register" className="btn-primary text-sm px-4 py-2" data-testid="checker-register-btn">
              Start a case
            </Link>
          </div>
        </div>
      </header>

      {/* Hero + form */}
      <section className="py-14 sm:py-16">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-center mb-10">
            <div className="pill pill-ai mb-4 inline-flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Free · No signup
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold mb-3" style={{ color: "var(--text-primary)" }}>
              Where is your priority date <span style={{ color: "var(--action)" }}>right now?</span>
            </h1>
            <p className="text-[16px] leading-relaxed max-w-2xl mx-auto" style={{ color: "var(--text-secondary)" }}>
              Enter your priority date, preference category, and country of chargeability.
              We instantly compare it against the current U.S. State Department Visa Bulletin
              and tell you if you're current, backlogged, or unavailable.
            </p>
          </div>

          {/* Form */}
          <form
            onSubmit={check}
            className="card-surface p-6 sm:p-8 mb-8"
            style={{ background: "#FFFFFF" }}
            data-testid="checker-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="field-label">Priority date</label>
                <input
                  type="date"
                  className="field-input"
                  value={form.priority_date}
                  onChange={(e) => setForm((f) => ({ ...f, priority_date: e.target.value }))}
                  data-testid="checker-priority-date"
                  required
                />
                <p className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>
                  From your I-130 / I-140 / ETA-9089 receipt.
                </p>
              </div>
              <div>
                <label className="field-label">Country of chargeability</label>
                <select
                  className="field-input"
                  value={form.country}
                  onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                  data-testid="checker-country"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
                <p className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>
                  Usually the beneficiary's country of birth.
                </p>
              </div>
              <div className="sm:col-span-2">
                <label className="field-label">Preference category</label>
                <select
                  className="field-input"
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  data-testid="checker-category"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {err && (
              <div className="mt-3 text-sm p-2 rounded" style={{ background: "#FEE2E2", color: "#7F1D1D" }}>
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full mt-5 text-base py-3"
              data-testid="checker-submit"
            >
              {busy ? "Checking…" : "Check my priority date"}
            </button>
          </form>

          {/* Result */}
          {result && sev && (
            <div
              className="rounded-lg border p-6 anim-in"
              style={{ background: sev.bg, borderColor: sev.border }}
              data-testid="checker-result"
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-11 h-11 rounded-md flex items-center justify-center flex-shrink-0"
                  style={{ background: sev.text, color: "#FFFFFF" }}
                >
                  <sev.icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="font-display font-extrabold text-lg" style={{ color: sev.text }}>
                      {headlineFor(result)}
                    </span>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold"
                      style={{ background: "rgba(255,255,255,.7)", color: sev.text }}
                    >
                      {result.category} · {result.country}
                    </span>
                  </div>
                  <p className="text-[14.5px] leading-relaxed mb-4" style={{ color: sev.text }}>
                    {result.banner_message}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs" style={{ color: sev.text }}>
                    <div><span style={{ opacity: 0.7 }}>Your priority date:</span> <b>{result.priority_date}</b></div>
                    <div><span style={{ opacity: 0.7 }}>Final Action Date cutoff:</span> <b>{result.fad_cutoff}</b></div>
                    {result.dff_cutoff && (
                      <div><span style={{ opacity: 0.7 }}>Dates-for-Filing cutoff:</span> <b>{result.dff_cutoff}</b></div>
                    )}
                    {result.bulletin_month && (
                      <div><span style={{ opacity: 0.7 }}>Based on:</span> <b>{result.bulletin_month} Visa Bulletin</b></div>
                    )}
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div
                className="mt-6 p-4 rounded-md flex flex-wrap items-center justify-between gap-3"
                style={{ background: "rgba(255,255,255,.55)" }}
              >
                <div className="text-sm" style={{ color: sev.text }}>
                  <b>Get a monthly email</b> the moment the next Visa Bulletin drops — no more manual checking.
                </div>
                <Link
                  to="/register"
                  className="btn-primary inline-flex items-center gap-2 text-sm px-4 py-2"
                  data-testid="checker-result-cta"
                >
                  Save & track this case <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {result.bulletin_source_url && (
                <a
                  href={result.bulletin_source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-semibold"
                  style={{ color: sev.text, opacity: 0.85 }}
                >
                  Read the full Visa Bulletin at travel.state.gov <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Trust strip */}
      <section
        className="py-14 border-t"
        style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}
      >
        <div className="mx-auto max-w-4xl px-6">
          <div className="grid sm:grid-cols-3 gap-6 text-center">
            <div>
              <div className="w-11 h-11 rounded-md mx-auto mb-2 flex items-center justify-center"
                   style={{ background: "#DCFCE7" }}>
                <Calendar className="w-5 h-5" style={{ color: "#166534" }} />
              </div>
              <div className="font-display font-bold text-sm mb-1" style={{ color: "var(--text-primary)" }}>
                Updated every month
              </div>
              <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                We auto-fetch the new Visa Bulletin on the 15th of every month, straight from travel.state.gov.
              </div>
            </div>
            <div>
              <div className="w-11 h-11 rounded-md mx-auto mb-2 flex items-center justify-center"
                   style={{ background: "#EFF6FF" }}>
                <ShieldCheck className="w-5 h-5" style={{ color: "#1E40AF" }} />
              </div>
              <div className="font-display font-bold text-sm mb-1" style={{ color: "var(--text-primary)" }}>
                No signup for the checker
              </div>
              <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                This tool is free forever. Sign up only when you want monthly email alerts and to save your case.
              </div>
            </div>
            <div>
              <div className="w-11 h-11 rounded-md mx-auto mb-2 flex items-center justify-center"
                   style={{ background: "#FEF3C7" }}>
                <Info className="w-5 h-5" style={{ color: "#78350F" }} />
              </div>
              <div className="font-display font-bold text-sm mb-1" style={{ color: "var(--text-primary)" }}>
                All F & EB categories
              </div>
              <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                F1, F2A, F2B, F3, F4, EB-1, EB-2, EB-3, EB-3 Other, EB-4, EB-5 — with per-country charts for China, India, Mexico, Philippines.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO body content (also anchors search-engine ranking) */}
      <section className="py-14 border-t" style={{ borderColor: "var(--border-stone)" }}>
        <div className="mx-auto max-w-3xl px-6 prose-sm" style={{ color: "var(--text-primary)" }}>
          <h2 className="font-display text-2xl font-extrabold mb-3">How the Visa Bulletin works</h2>
          <p className="text-sm leading-relaxed mb-3" style={{ color: "var(--text-secondary)" }}>
            The U.S. Department of State publishes the Visa Bulletin monthly. It lists
            the current cutoff date (called the "Final Action Date") for each
            preference category. If <b>your priority date is on or before the cutoff</b>,
            you're "current" — the visa number is available and your case can move
            forward. Otherwise you're waiting in line.
          </p>
          <p className="text-sm leading-relaxed mb-3" style={{ color: "var(--text-secondary)" }}>
            Every category has separate cutoffs for beneficiaries from China, India,
            Mexico, and the Philippines because those countries hit the annual
            per-country cap and have their own longer lines. Everyone else falls
            under "All Chargeability Areas Except Those Listed" — the shorter line.
          </p>

          <h2 className="font-display text-2xl font-extrabold mb-3 mt-8">Common questions</h2>
          <details className="mb-3 p-3 rounded border" style={{ borderColor: "var(--border-stone)" }}>
            <summary className="cursor-pointer font-semibold text-sm">Where do I find my priority date?</summary>
            <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
              On your Form I-797 receipt notice (from USCIS) after filing I-130
              (family) or I-140 (employment). For labor-cert-required EB cases,
              it's the date the ETA-9089 was <i>received</i> by DOL.
            </p>
          </details>
          <details className="mb-3 p-3 rounded border" style={{ borderColor: "var(--border-stone)" }}>
            <summary className="cursor-pointer font-semibold text-sm">What's the difference between "Final Action Dates" and "Dates for Filing"?</summary>
            <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
              Final Action Dates (FAD) is the date at which USCIS or a consulate can
              actually approve you and give you the green card. Dates for Filing (DFF)
              is an earlier date that lets you submit documents but not yet be
              adjudicated. USCIS decides each month which chart to honor. This
              checker shows both when available.
            </p>
          </details>
          <details className="mb-3 p-3 rounded border" style={{ borderColor: "var(--border-stone)" }}>
            <summary className="cursor-pointer font-semibold text-sm">Can I "cross-charge" to a spouse's country?</summary>
            <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
              Sometimes. If your spouse was born in a country with shorter waits,
              you can charge your case to that country instead. Ask your attorney
              about eligibility.
            </p>
          </details>
          <details className="mb-3 p-3 rounded border" style={{ borderColor: "var(--border-stone)" }}>
            <summary className="cursor-pointer font-semibold text-sm">What does "Current" mean?</summary>
            <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
              "Current" (shown as "C" in the bulletin) means the visa number is
              immediately available — no more waiting in line for that category and
              country. You can proceed with adjustment of status or consular
              processing right away.
            </p>
          </details>
          <details className="mb-3 p-3 rounded border" style={{ borderColor: "var(--border-stone)" }}>
            <summary className="cursor-pointer font-semibold text-sm">What if my category becomes "Unavailable" (U)?</summary>
            <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
              This happens when the annual visa cap for a category is hit before
              the fiscal year ends. Numbers usually reopen at the start of the
              next fiscal year (October 1). Your priority date is preserved —
              you just wait longer.
            </p>
          </details>
        </div>
      </section>

      {/* Second CTA — final funnel */}
      <section className="py-14 border-t" style={{ background: "var(--bg-app)", borderColor: "var(--border-stone)" }}>
        <div className="mx-auto max-w-2xl px-6 text-center">
          <h2 className="font-display text-3xl font-extrabold mb-3" style={{ color: "var(--text-primary)" }}>
            Want a monthly email <span style={{ color: "var(--action)" }}>every time the bulletin drops?</span>
          </h2>
          <p className="text-sm leading-relaxed mb-6" style={{ color: "var(--text-secondary)" }}>
            Sign up (free) → save your priority date on a real case → get an automated
            email on the 15th of every month showing your status. Also unlocks the
            full I-130 / I-140 / AoS filing workflow, document AI, and attorney review.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/register"
              className="btn-primary inline-flex items-center gap-2 text-base px-6 py-3"
              data-testid="checker-final-cta"
            >
              Create free account <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/sample-packet"
              className="btn-ghost inline-flex items-center gap-2 text-base px-6 py-3"
              style={{ color: "var(--action)" }}
            >
              See sample filing packet
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t py-8" style={{ borderColor: "var(--border-stone)", background: "#FFFFFF" }}>
        <div className="mx-auto max-w-6xl px-6 flex flex-wrap gap-4 justify-between items-center text-xs" style={{ color: "var(--text-secondary)" }}>
          <div>© {new Date().getFullYear()} DS-260USA.com — Priority Date Checker</div>
          <div className="flex gap-4 flex-wrap">
            <Link to="/" className="hover:opacity-80">Home</Link>
            <Link to="/faq" className="hover:opacity-80">FAQ</Link>
            <Link to="/sample-packet" className="hover:opacity-80">Sample packet</Link>
            <Link to="/adjustment-of-status" className="hover:opacity-80">AoS module</Link>
            <Link to="/legal/privacy" className="hover:opacity-80">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
